/* Homepage-only components; safe to reconnect when Shopify re-renders a section. */
(() => {
  if (!window.customElements) return;
  if (!customElements.get('mr-home-hero')) {
    class MRHomeHero extends HTMLElement {
      connectedCallback() {
        if (this._connected) return;
        this._connected = true;
        this._abort = new AbortController();
        this._slides = Array.from(this.querySelectorAll('[data-mrh-slide]'));
        this._media = Array.from(this.querySelectorAll('[data-mrh-media]'));
        this._selectors = Array.from(this.querySelectorAll('[data-mrh-select]'));
        this._progress = this._selectors.map(button => button.querySelector('.mrh-progress'));
        this._selection = 0;
        this._hideTimers = new Set();
        this._play = this.querySelector('[data-mrh-play]');
        this._count = this.querySelector('[data-mrh-current]');
        this._announcement = this.querySelector('[data-mrh-announcement]');
        this._motion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this._index = 0;
        this._elapsed = 0;
        this._timer = null;
        this._prefetchTimer = null;
        this._startedAt = null;
        this._progressAnimation = null;
        this._loading = false;
        this._inView = true;
        this._hovered = false;
        this._focused = false;
        this._menuOpen = false;
        this._interval = Math.max(5000, Number(this.dataset.interval) || 9000);
        this._autoplayPending = this._slides.length > 1 && this.dataset.autoplay === 'true' && !this._motion.matches && !window.Shopify?.designMode;
        this._playing = false;
        this._engaged = false;
        if (!this._slides.length) return;
        const listen = (node, type, fn, options = {}) => node?.addEventListener(type, fn, {...options, signal:this._abort.signal});
        // Keep the first photograph stable while the visitor initially reads it.
        // Later slides and their downloads start only after visitor interaction.
        const engage = event => {
          if (this._engaged) return;
          this._engaged = true;
          const control = event.target instanceof Element && event.target.closest('[data-mrh-select],[data-mrh-previous],[data-mrh-next],[data-mrh-play]');
          if (this._autoplayPending && !control && !this._motion.matches) this._playing = true;
          this._autoplayPending = false;
          this.syncPlayback();
        };
        ['pointerdown', 'keydown', 'wheel', 'touchmove'].forEach(type => listen(window, type, engage, {once:true, passive:true}));
        listen(this, 'click', event => {
          const selector = event.target.closest('[data-mrh-select]');
          if (selector) this.select(Number(selector.dataset.mrhSelect), true);
          else if (event.target.closest('[data-mrh-previous]')) this.select(this._index - 1, true);
          else if (event.target.closest('[data-mrh-next]')) this.select(this._index + 1, true);
          else if (event.target.closest('[data-mrh-play]')) {
            this._autoplayPending = false;
            this._playing = !this._playing;
            this._hovered = false;
            this._focused = false;
            this.syncPlayback();
          }
        });
        listen(this, 'keydown', event => {
          if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || event.target.closest('input,textarea,select')) return;
          if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
            event.preventDefault();
            this.select(this._index + (event.key === 'ArrowRight' ? 1 : -1), true);
          }
        });
        listen(this, 'pointerenter', event => { if (event.pointerType === 'mouse') { this._hovered = true; this.syncPlayback(); } });
        listen(this, 'pointerleave', event => { if (event.pointerType === 'mouse') { this._hovered = false; this.syncPlayback(); } });
        listen(this, 'focusin', () => { this._focused = true; this.syncPlayback(); });
        listen(this, 'focusout', event => { if (!this.contains(event.relatedTarget)) { this._focused = false; this.syncPlayback(); } });
        listen(this, 'touchstart', event => { this._touch = event.touches.length === 1 ? {x:event.touches[0].clientX,y:event.touches[0].clientY} : null; }, {passive:true});
        listen(this, 'touchcancel', () => { this._touch = null; }, {passive:true});
        listen(this, 'touchend', event => {
          if (!this._touch || !event.changedTouches.length) return;
          const dx = event.changedTouches[0].clientX - this._touch.x;
          const dy = event.changedTouches[0].clientY - this._touch.y;
          this._touch = null;
          if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.4) this.select(this._index + (dx < 0 ? 1 : -1), true);
        }, {passive:true});
        listen(document, 'visibilitychange', () => this.syncPlayback());
        listen(document, 'mrh:menu-toggle', event => { this._menuOpen = event.detail.open; this.syncPlayback(); });
        listen(this._motion, 'change', () => { if (this._motion.matches) this._playing = false; this.syncPlayback(); });
        listen(document, 'shopify:block:select', event => {
          if (String(event.detail?.sectionId) !== this.dataset.sectionId) return;
          const index = this._slides.findIndex(slide => slide.dataset.blockId === String(event.detail.blockId));
          if (index >= 0) this.select(index, true);
        });
        listen(document, 'shopify:section:select', event => {
          if (String(event.detail?.sectionId) === this.dataset.sectionId) { this._playing = false; this.syncPlayback(); }
        });
        if ('IntersectionObserver' in window) {
          this._observer = new IntersectionObserver(entries => { this._inView = entries[0].isIntersecting; this.syncPlayback(); }, {threshold:0.05});
          this._observer.observe(this);
        }
        this.syncPlayback();
        this.classList.add('is-ready');
      }
      loadMedia(index) {
        const image = this._media[index]?.querySelector('img');
        if (!image) return Promise.resolve(true);
        if (image.complete && image.naturalWidth > 0) return Promise.resolve(true);
        // A real src/srcset stays in the HTML; only hidden slides are lazy.
        return new Promise(resolve => {
          let settled = false;
          const finish = success => {
            if (settled) return;
            settled = true;
            clearTimeout(timeout);
            image.removeEventListener('load', loaded);
            image.removeEventListener('error', failed);
            this._abort.signal.removeEventListener('abort', failed);
            resolve(success);
          };
          const loaded = () => finish(true);
          const failed = () => finish(false);
          const timeout = setTimeout(failed, 10000);
          image.addEventListener('load', loaded, {once:true});
          image.addEventListener('error', failed, {once:true});
          this._abort.signal.addEventListener('abort', failed, {once:true});
          image.loading = 'eager';
        });
      }
      select(index, manual) {
        if (!this._slides?.length) return;
        const next = (index + this._slides.length) % this._slides.length;
        const selection = ++this._selection;
        if (manual) { this._playing = false; this._autoplayPending = false; }
        this._loading = true;
        this.syncPlayback();
        return this.loadMedia(next).then(loaded => {
          if (!this._connected || selection !== this._selection) return;
          this._loading = false;
          if (!loaded) {
            if (manual && this._announcement) this._announcement.textContent = 'Photograph could not load. Please try again.';
            this._elapsed = 0;
            this.syncPlayback();
            return;
          }
          this._index = next;
          this._elapsed = 0;
          this._slides.forEach((slide, i) => { slide.hidden = i !== next; slide.classList.toggle('is-active', i === next); });
          this._media.forEach((media, i) => {
            if (i === next) media.hidden = false;
            // A single frame allows the incoming photo to crossfade from opacity 0.
            if (i !== next) media.classList.remove('is-active');
          });
          requestAnimationFrame(() => {
            if (!this._connected || selection !== this._selection) return;
            this._media[next]?.classList.add('is-active');
          });
          const hideTimer = setTimeout(() => {
            this._media.forEach((media, i) => { if (i !== this._index) media.hidden = true; });
            this._hideTimers.delete(hideTimer);
          }, 1100);
          this._hideTimers.add(hideTimer);
          this._selectors.forEach((button, i) => { button.setAttribute('aria-pressed', String(i === next)); button.classList.toggle('is-selected', i === next); });
          if (this._count) this._count.textContent = String(next + 1).padStart(2,'0');
          if (manual && this._announcement) this._announcement.textContent = `${this._slides[next].dataset.title}, slide ${next + 1} of ${this._slides.length}`;
          this.syncPlayback();
        });
      }
      syncPlayback() {
        if (this._startedAt !== null) this._elapsed += performance.now() - this._startedAt;
        this._startedAt = null;
        clearTimeout(this._timer);
        clearTimeout(this._prefetchTimer);
        this._progressAnimation?.cancel();
        this._progressAnimation = null;
        if (!this._connected || !this._slides?.length) return;
        if (this._play) {
          this._play.setAttribute('aria-label', this._playing ? 'Pause slideshow' : 'Play slideshow');
          this._play.querySelector('[data-mrh-play-icon]').hidden = this._playing;
          this._play.querySelector('[data-mrh-pause-icon]').hidden = !this._playing;
        }
        this.paintProgress();
        if (!this._playing || this._loading || this._hovered || this._focused || this._menuOpen || !this._inView || document.hidden) return;
        const remaining = Math.max(0, this._interval - this._elapsed);
        const fill = this._progress[this._index];
        const fraction = Math.min(1, this._elapsed / this._interval);
        this._startedAt = performance.now();
        if (fill?.animate) this._progressAnimation = fill.animate(
          [{transform:`scaleX(${fraction})`}, {transform:'scaleX(1)'}],
          {duration:remaining, easing:'linear', fill:'forwards'}
        );
        this._timer = setTimeout(() => this.select(this._index + 1, false), remaining);
        // Prepare only the next photograph shortly before it is needed.
        this._prefetchTimer = setTimeout(() => this.loadMedia((this._index + 1) % this._slides.length), Math.max(0, remaining - 2500));
      }
      paintProgress() {
        this._progress.forEach((fill, i) => {
          if (fill) fill.style.transform = `scaleX(${i === this._index ? (this._playing ? Math.min(1, this._elapsed / this._interval) : 1) : 0})`;
        });
      }
      disconnectedCallback() {
        this._connected = false;
        this._selection++;
        this._abort?.abort();
        this._observer?.disconnect();
        clearTimeout(this._timer);
        clearTimeout(this._prefetchTimer);
        this._hideTimers.forEach(clearTimeout);
        this._progressAnimation?.cancel();
      }
    }
    customElements.define('mr-home-hero', MRHomeHero);
  }
  if (!customElements.get('mr-home-header')) {
    class MRHomeHeader extends HTMLElement {
      connectedCallback() {
        if (this._connected) return;
        this._connected = true;
        this._abort = new AbortController();
        this._dialog = this.querySelector('dialog');
        this._open = this.querySelector('[data-mrh-open-menu]');
        const listen = (node, type, fn) => node?.addEventListener(type, fn, {signal:this._abort.signal});
        listen(this._open, 'click', () => {
          if (this._dialog.open) return;
          this._dialog.showModal();
          this._open.setAttribute('aria-expanded','true');
          this._priorOverflow = document.documentElement.style.overflow;
          document.documentElement.style.overflow = 'hidden';
          this._locked = true;
          document.dispatchEvent(new CustomEvent('mrh:menu-toggle',{detail:{open:true}}));
        });
        listen(this.querySelector('[data-mrh-close-menu]'), 'click', () => this._dialog.close());
        listen(this._dialog, 'click', event => {
          if (event.target !== this._dialog) return;
          const bounds = this._dialog.getBoundingClientRect();
          if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this._dialog.close();
        });
        listen(this._dialog, 'close', () => {
          this.releaseScroll();
          this._open.setAttribute('aria-expanded','false');
          document.dispatchEvent(new CustomEvent('mrh:menu-toggle',{detail:{open:false}}));
          this._open.focus({preventScroll:true});
        });
      }
      releaseScroll() {
        if (this._locked) document.documentElement.style.overflow = this._priorOverflow;
        this._locked = false;
      }
      disconnectedCallback() {
        this._connected = false;
        this._abort?.abort();
        if (this._dialog?.open) this._dialog.close();
        this.releaseScroll();
        document.dispatchEvent(new CustomEvent('mrh:menu-toggle',{detail:{open:false}}));
      }
    }
    customElements.define('mr-home-header', MRHomeHeader);
  }
})();
